import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button, Input, Flex, Steps, message } from 'antd';
import {
  ShieldCheck,
  Camera,
  CheckCircle2,
  Upload,
} from 'lucide-react';
import axios from 'axios';
import './VerificationModal.scss';

interface VerificationModalProps {
  isVisible: boolean;
  otpRequired: boolean;
  selfieRequired: boolean;
  onComplete: (_data: { otp?: string; selfieUrl?: string }) => void;
  onCancel: () => void;
  loading?: boolean;
}

const VerificationModal: React.FC<VerificationModalProps> = ({
  isVisible,
  otpRequired,
  selfieRequired,
  onComplete,
  onCancel,
  loading = false,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [otpValue, setOtpValue] = useState('');
  const [selfieImage, setSelfieImage] = useState<string | null>(null);
  const [selfieUrl, setSelfieUrl] = useState<string>('');
  const [cameraStream, setCameraStream] =
    useState<MediaStream | null>(null);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const steps = [];
  if (otpRequired) steps.push('otp');
  if (selfieRequired) steps.push('selfie');

  useEffect(() => {
    if (!isVisible) {
      setCurrentStep(0);
      setOtpValue('');
      setSelfieImage(null);
      setSelfieUrl('');
      stopCamera();
    }
  }, [isVisible]);

  useEffect(() => {
    return () => stopCamera();
  }, []);

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
      setIsCameraActive(false);
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 640 },
          height: { ideal: 480 },
        },
      });
      setCameraStream(stream);
      setIsCameraActive(true);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('Camera access error:', error);
      message.error(
        'Unable to access camera. Please check permissions.',
      );
    }
  };

  const uploadImageToServer = async (
    imageData: string,
  ): Promise<string> => {
    try {
      setIsUploading(true);

      const response = await fetch(imageData);
      const blob = await response.blob();

      const formData = new FormData();
      formData.append('file', blob, 'selfie.jpg');

      const uploadResponse = await axios.post(
        'https://file.myg.money/api/files',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );

      if (
        uploadResponse.data.status === 201 &&
        uploadResponse.data.data?.[0]?.url
      ) {
        message.success('Selfie uploaded successfully');
        return uploadResponse.data.data[0].url;
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      message.error('Failed to upload selfie. Please try again.');
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  const capturePhoto = async () => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const video = videoRef.current;
      const context = canvas.getContext('2d');

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      if (context) {
        context.scale(-1, 1);
        context.drawImage(video, -canvas.width, 0);
        context.scale(-1, 1);
        const imageData = canvas.toDataURL('image/jpeg', 0.8);
        setSelfieImage(imageData);
        stopCamera();

        try {
          const uploadedUrl = await uploadImageToServer(imageData);
          setSelfieUrl(uploadedUrl);
        } catch (error) {
          setSelfieImage(null);
        }
      }
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      if (steps[currentStep + 1] === 'selfie' && !selfieImage) {
        startCamera();
      }
    } else {
      handleComplete();
    }
  };

  const handleComplete = () => {
    onComplete({
      otp: otpRequired ? otpValue : undefined,
      selfieUrl: selfieRequired ? selfieUrl : undefined,
    });
  };

  const canProceed = () => {
    const currentStepType = steps[currentStep];
    if (currentStepType === 'otp') return otpValue.length === 6;
    if (currentStepType === 'selfie') return !!selfieUrl;
    return false;
  };

  const renderOTPStep = () => (
    <div className="verification-step-content">
      <div className="verification-icon-wrapper">
        <ShieldCheck size={48} strokeWidth={2} />
      </div>
      <h3 className="verification-title">Enter OTP Code</h3>
      <p className="verification-description">
        Please enter the 6-digit code sent to your phone
      </p>
      <div className="otp-input-wrapper">
        <Input.OTP
          length={6}
          size="large"
          value={otpValue}
          onChange={setOtpValue}
          className="verification-otp"
          type="password"
        />
      </div>
    </div>
  );

  const renderSelfieStep = () => (
    <div className="verification-step-content">
      <div className="verification-icon-wrapper camera">
        <Camera size={48} strokeWidth={2} />
      </div>
      <h3 className="verification-title">Take a Selfie</h3>
      <p className="verification-description">
        Please take a clear photo of yourself
      </p>

      <div className="selfie-container">
        {!selfieImage ? (
          <div className="camera-view">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="camera-video"
            />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            <Button
              type="primary"
              size="large"
              icon={
                isUploading ? (
                  <Upload size={18} />
                ) : (
                  <Camera size={18} />
                )
              }
              onClick={capturePhoto}
              disabled={!isCameraActive || isUploading}
              loading={isUploading}
              className="capture-btn"
            >
              {isUploading ? 'Uploading...' : 'Capture'}
            </Button>
          </div>
        ) : (
          <div className="selfie-preview">
            <img
              src={selfieImage}
              alt="Selfie"
              className="selfie-image"
            />
            <div className="selfie-check">
              <CheckCircle2 size={32} strokeWidth={2.5} />
            </div>
            <Button
              size="large"
              onClick={() => {
                setSelfieImage(null);
                setSelfieUrl('');
                startCamera();
              }}
              className="retake-btn"
              disabled={isUploading}
            >
              Retake
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  const renderStep = () => {
    const currentStepType = steps[currentStep];
    if (currentStepType === 'otp') return renderOTPStep();
    if (currentStepType === 'selfie') return renderSelfieStep();
    return null;
  };

  return (
    <Modal
      open={isVisible}
      onCancel={onCancel}
      footer={null}
      width="min(500px, 95vw)"
      centered
      closable={!loading && !isUploading}
      maskClosable={false}
    >
      <div className="verification-modal-content">
        {steps.length > 1 && (
          <Steps
            current={currentStep}
            items={steps.map((step) => ({
              title: step === 'otp' ? 'OTP' : 'Selfie',
            }))}
            className="verification-steps"
          />
        )}

        {renderStep()}

        <Flex gap={12} style={{ marginTop: 24 }}>
          {currentStep > 0 && (
            <Button
              size="large"
              onClick={() => {
                stopCamera();
                setCurrentStep(currentStep - 1);
              }}
              disabled={loading || isUploading}
              style={{ flex: 1 }}
            >
              Back
            </Button>
          )}
          <Button
            type="primary"
            size="large"
            onClick={handleNext}
            loading={loading || isUploading}
            disabled={!canProceed()}
            style={{ flex: 1 }}
          >
            {currentStep === steps.length - 1 ? 'Complete' : 'Next'}
          </Button>
        </Flex>
      </div>
    </Modal>
  );
};

export default VerificationModal;
